import { useTranslation } from '@bms/common';
import type { Alarm, GRMSLiveRoomState, Room } from '@bms/common';
import { useSiteDates } from '@/components/checkin/deskRules';

// ============================================================================
// Room Alarm Computation (§9) — client-side v1
// Derives display-only alarms from current room state, to preview a condition
// before Edge raises the operational alarm (spec §5.3). Auto-clearing: they
// appear and disappear with the condition.
//
// HVAC deviation is not computed here (docs/decisions/room-dialog.md D19): Edge
// owns off, grace and snooze, so the UI colours a temperature only from the
// room's `hvac_deviation` alarm, read with `hvacDeviationState`.
// ============================================================================

export interface RoomAlarm {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

/** The one shape this module needs from `useTranslation()['t']`. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Used only when a caller does not pass a real `t` (the unit tests below,
 * which exercise the alarm arithmetic without mounting i18n). Production
 * rendering always supplies the live translator, so a room's alarm messages
 * are never stuck in English under a non-English locale.
 */
const englishFallbackTranslate: Translate = (key) => {
  if (key === 'grms.alarms.motionInVacant') return 'Motion detected in vacant room';
  return key;
};

/** What the room's `hvac_deviation` alarm says about one zone's temperature. */
export type HvacDeviationState =
  | { kind: 'none' }
  /** Open and not acknowledged: the temperature is red. */
  | { kind: 'active' }
  /** Acknowledged: plain colour until `until` (null when the server sent no end), when Edge checks again. */
  | { kind: 'snoozed'; until: string | null; by: string | null };

type DeviationAlarm = Pick<Alarm, 'alarmType' | 'status' | 'acknowledgedBy' | 'snoozedUntil' | 'zone'>;

/**
 * An acknowledged `hvac_deviation` is snoozed (W3): hidden everywhere until Edge checks again (it returns
 * as `active`) — the red temperature, the room card marker and count, the Attention row, the dialog's
 * alarm banner, and the vacant "Ready" rule (owner, 2026-10-24). Takes a polled alarm or a live event.
 */
export function isSnoozedDeviation(alarm: { alarmType: string; status: string }): boolean {
  return alarm.alarmType === 'hvac_deviation' && alarm.status === 'acknowledged';
}

/**
 * The state of a zone's temperature from the room's alarms. An alarm without a zone is room-level and
 * covers every zone; asking without a zone (the room card, the strip) matches any zone's alarm. An
 * open alarm outranks a snoozed one, and a resolved alarm says nothing.
 */
export function hvacDeviationState(
  alarms: readonly DeviationAlarm[] | null | undefined,
  zone?: number | null,
): HvacDeviationState {
  const mine = (alarms ?? []).filter(alarm =>
    alarm.alarmType === 'hvac_deviation'
    && (alarm.status === 'active' || alarm.status === 'acknowledged')
    && (alarm.zone == null || zone == null || alarm.zone === zone));
  if (mine.some(alarm => alarm.status === 'active')) return { kind: 'active' };
  const snoozed = mine.find(alarm => alarm.status === 'acknowledged');
  return snoozed
    ? { kind: 'snoozed', until: snoozed.snoozedUntil ?? null, by: snoozed.acknowledgedBy ?? null }
    : { kind: 'none' };
}

export interface HvacDeviationTone {
  /** True only for an open, unacknowledged deviation alarm. */
  red: boolean;
  /** "Acknowledged by {who} · checks again {time}" on a snoozed temperature. */
  title?: string;
}

/** `hvacDeviationState` as a colour and a hover, the time in the site's date format. */
export function useHvacDeviationTone(
  alarms: readonly DeviationAlarm[] | null | undefined,
): (zone?: number | null) => HvacDeviationTone {
  const { t } = useTranslation();
  const { stamp } = useSiteDates();
  return (zone) => {
    const state = hvacDeviationState(alarms, zone);
    if (state.kind === 'active') return { red: true };
    if (state.kind === 'snoozed' && state.until && state.by) {
      return { red: false, title: t('grms.alarms.hvacSnoozed', { who: state.by, time: stamp(state.until) }) };
    }
    return { red: false };
  };
}

/**
 * Compute display-only alarms for a room from its current state.
 * All data comes from the Room aggregates plus the optional MQTT live overlay.
 */
export function computeRoomAlarms(
  room: Room,
  liveState?: GRMSLiveRoomState,
  t: Translate = englishFallbackTranslate,
): RoomAlarm[] {
  const alarms: RoomAlarm[] = [];

  const occupancy = liveState?.occupancyStatus ?? room.occupancyStatus ?? 'vacant';
  const presence = liveState?.presenceDetected ?? room.presenceDetected ?? false;

  // §9.1 — Motion in Empty Room
  // Presence detected in a vacant room → info alarm
  if (presence && occupancy === 'vacant') {
    alarms.push({
      type: 'motion_in_vacant',
      severity: 'info',
      message: t('grms.alarms.motionInVacant'),
    });
  }

  return alarms;
}

/**
 * Count total alarms across a list of rooms.
 */
export function countAlarms(
  rooms: Room[],
  liveStates: Map<string, GRMSLiveRoomState>,
  t?: Translate,
): number {
  let count = 0;
  for (const room of rooms) {
    count += computeRoomAlarms(room, liveStates.get(room.roomCode), t).length;
  }
  return count;
}

/**
 * Check if a room has any active alarms.
 */
export function hasAlarms(
  room: Room,
  liveState?: GRMSLiveRoomState,
  t?: Translate,
): boolean {
  return computeRoomAlarms(room, liveState, t).length > 0;
}

// ============================================================================
// alarms:write gate — moved out of this file
//
// Acknowledge and Resolve both require `alarms:write` (GRMS spec section 9.3).
// The gate used to live here as a hardcoded role list — `superadmin`,
// `site_admin`, `manager` — transcribed from `schema.sql`'s seeded grants. That
// was correct only for a site that had never regranted the permission, and it
// failed in both directions: hiding the actions from a role a site had granted,
// and offering them to a role whose grant had been revoked. `User` carried no
// permissions, so there was nothing better to read.
//
// There is now: the Current-user permission projection. Use `usePermissions()`
// from `@bms/common` and read `canWriteAlarms` off it, which also clears a
// stale site's answer, revalidates while the control is visible, and fails
// closed when the read fails. The pure helper behind it is
// `@bms/common`'s `types/permissions.ts`.
// ============================================================================
